import { useId } from "react";
import { useTranslation } from "react-i18next";
import AnchoredTooltip from "../ui/overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../ui/overlays/useAnchoredTooltip";
import { classNames } from "../ui/styles/classNames";
import type { ServerConnectionStatus } from "./serverConnection";
import { useServerConnection } from "./useServerConnection";

const DOT_CLASS: Record<ServerConnectionStatus, string> = {
  checking: "",
  connected: "app-connection-dot-connected",
  databaseUnavailable: "app-connection-dot-degraded",
  faulty: "app-connection-dot-degraded",
  unreachable: "app-connection-dot-offline",
};

/**
 * Top bar indicator of the server connection, from the readiness check. Click to re-check.
 * Hovering or focusing it explains the status; screen readers get the same explanation.
 */
export default function ConnectionIndicator({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { connection, checkNow } = useServerConnection();
  const tooltip = useAnchoredTooltip();
  const explanationId = useId();
  const label = t(`connection.status.${connection.status}`);
  const explanation = t(`connection.explanation.${connection.status}`);

  return (
    <span className={classNames("inline-flex items-center", className)}>
      <button
        type="button"
        onClick={checkNow}
        className="app-topbar-action app-topbar-nav-button"
        aria-label={t("connection.checkAgainAria", { status: label })}
        aria-describedby={explanationId}
        {...tooltip.hoverProps()}
        {...tooltip.focusProps()}
      >
        <span className={classNames("app-connection-dot", DOT_CLASS[connection.status])} aria-hidden="true" />
        <span className="hidden 2xl:inline">{label}</span>
      </button>
      <span id={explanationId} className="sr-only">{explanation}</span>
      {tooltip.shown === null ? null : (
        <AnchoredTooltip anchor={tooltip.shown.anchor} className="w-64">
          <span className="block font-medium">{label}</span>
          <span className="block">{explanation}</span>
        </AnchoredTooltip>
      )}
    </span>
  );
}
